import DataGrid, { Column, Format, Summary, TotalItem } from "devextreme-react/data-grid";
import Popup, { ToolbarItem } from "devextreme-react/popup";
import { formatNumber } from "devextreme/localization";
import { useState } from "react";
import { LoadPopupPanel } from "../../../../components";
import { showErrorMessage } from "../../../../utils/messages";
import { RetailOrderReportType } from "../../../cfi/retail-order";
import { apiGenerateRetailOrderReport } from "../../../common/retail-order-report/api/get-retail-order-report";
import { Buffer } from 'buffer';
import { useAuth } from "../../../../contexts/auth";
import { useGetCompany } from "../../../../api/company";
import { BusinessFlow } from "../../../../types/enums";
import { RetailOrderDetails } from "../types/RetailOrderDetails";

export default function ChangeRequestPopup(props: {
	orderDetails: RetailOrderDetails,
	title: string,
	onHidePopup: () => void
}) {

	let [loading, setLoading] = useState(false);

	const { user } = useAuth();

	const calculateCustomSummary = (options: any) => {

		if (options.name === "retailPriceDifference") {
			if (options.summaryProcess === "finalize") {
				options.totalValue = props.orderDetails.changeOrderProducts
					.reduce((sum, x) => sum + x.SRE, 0) -
						props.orderDetails.products.filter(x => x.isGhostItem || !x.isGhosted)
							.reduce((sum, x) => sum + (x.dealerRetailExtended ?? 0), 0);
			}
		}

		if (options.name === "priceDifference") {
			if (options.summaryProcess === "finalize") {
				options.totalValue = props.orderDetails.changeOrderProducts
					.reduce((sum, x) => sum + x.extendedPrice, 0) -
						props.orderDetails.products.filter(x => x.isGhostItem || !x.isGhosted)
							.reduce((sum, x) => sum + x.extendedPrice, 0);
			}
		}
	}

	const userCompany = useGetCompany(user?.companyId);

	function printReport(reportType: RetailOrderReportType) {
		setLoading(true);

		apiGenerateRetailOrderReport(props.orderDetails.retailOrderID, reportType)
			.then((result) => {
				if (result.isSuccess) {
					const file = new Blob(
						[Buffer.from(result.data, "base64")],
						{ type: 'application/pdf' });

					const fileURL = URL.createObjectURL(file);
					window.open(fileURL);
				}
				else {
					showErrorMessage(result.errorMessage!);
				}
			})
			.catch((reason) => {
				showErrorMessage(reason);
			})
			.finally(() => { setLoading(false); });
	}

	return (
		<Popup
			visible={true}
			title={props.title}
			hideOnOutsideClick={true}
			onHiding={() => props.onHidePopup()}
		>
			<ToolbarItem
				options={{
					icon: 'dx-icon dx-icon-close',
					onClick: () => props.onHidePopup()
				}}
				widget="dxButton"
				location="after"
			/>
			<p>
				Original order: <b>{props.orderDetails.roNumber}</b>
			</p>
			<DataGrid
				dataSource={props.orderDetails.products.filter(x => x.isGhostItem || !x.isGhosted)}
				allowColumnReordering={true}
				allowColumnResizing={true}
				showBorders={true}
				showColumnLines={true}
				columnAutoWidth={false}
				keyExpr={'roProductID'}
			>
				<Column
					dataField={'itemDescription'}
					caption={'Item Description'}
					dataType="string"
					width={300}
					cssClass={'multi-line'}
				/>
				<Column
					dataField={'productLineDescription'}
					caption={'Product Line'}
					dataType="string"
				/>
				<Column
					dataField={'dealerSKU'}
					caption={'SKU'}
					dataType="string"
				/>
				<Column
					dataField={'dealerUOM'}
					caption={'UOM'}
					dataType="string"
				/>
				<Column
					dataField={'dealerQty'}
					caption={'Qty'}
					dataType="number"
				/>
				<Column
					dataField={'dealerRetail'}
					caption={'Retail Unit'}
					dataType="number"
				>
					<Format type="currency" precision={2} ></Format>
				</Column>
				<Column
					dataField={'dealerRetailExtended'}
					caption={'Retail Extended'}
					dataType="number"
					width={150}
				>
					<Format type="currency" precision={2} ></Format>
				</Column>
				{
					props.orderDetails.showDealerCost &&
					<Column
						dataField={'dealerPrice'}
						caption={'Cost Unit'}
						dataType="number"
					>
						<Format type="currency" precision={2} ></Format>
					</Column>
				}
				{
					props.orderDetails.showDealerCost &&
					<Column
						dataField={'extendedPrice'}
						caption={'Cost Extended'}
						dataType="number"
						width={150}
					>
						<Format type="currency" precision={2} ></Format>
					</Column>
				}
				<Summary>
					<TotalItem
						column="dealerRetailExtended"
						summaryType="sum"
						customizeText={(data: any) => 'Total: ' + formatNumber(data.value, { type: 'currency', precision: 2 })}
					/>
					<TotalItem
						column="extendedPrice"
						summaryType="sum"
						customizeText={(data: any) => 'Total: ' + formatNumber(data.value, { type: 'currency', precision: 2 })}
					/>
				</Summary>
			</DataGrid>
			<p>
				New order
			</p>
			<DataGrid
				dataSource={props.orderDetails.changeOrderProducts}
				allowColumnReordering={true}
				allowColumnResizing={true}
				showBorders={true}
				showColumnLines={true}
				columnAutoWidth={false}
				keyExpr={'roProductChangeRequestID'}
			>
				<Column
					dataField={'itemDescription'}
					caption={'Item Description'}
					dataType="string"
					width={300}
					cssClass={'multi-line'}
				/>
				<Column
					dataField={'productLineDescription'}
					caption={'Product Line'}
					dataType="string"
				/>
				<Column
					dataField={'dealerSKU'}
					caption={'SKU'}
					dataType="string"
				/>
				<Column
					dataField={'dealerUOM'}
					caption={'UOM'}
					dataType="string"
				/>
				<Column
					dataField={'dealerQty'}
					caption={'Qty'}
					dataType="number"
				/>
				<Column
					dataField={'SRU'}
					caption={'Retail Unit'}
					dataType="number"
				>
					<Format type="currency" precision={2} ></Format>
				</Column>
				<Column
					dataField={'SRE'}
					caption={'Retail Extended'}
					dataType="number"
					width={150}
				>
					<Format type="currency" precision={2} ></Format>
				</Column>
				{
					props.orderDetails.showDealerCost &&
					<Column
						dataField={'dealerPrice'}
						caption={'Cost Unit'}
						dataType="number"
					>
						<Format type="currency" precision={2} ></Format>
					</Column>
				}
				{
					props.orderDetails.showDealerCost &&
					<Column
						dataField={'extendedPrice'}
						caption={'Cost Extended'}
						dataType="number"
						width={150}
					>
						<Format type="currency" precision={2} ></Format>
					</Column>
				}
				<Summary calculateCustomSummary={calculateCustomSummary}>
					<TotalItem
						column="SRE"
						summaryType="sum"
						customizeText={(data: any) => 'Total: ' + formatNumber(data.value, { type: 'currency', precision: 2 })}
					/>
					<TotalItem
						column="SRE"
						name="retailPriceDifference"
						summaryType="custom"
						customizeText={(data: any) => 'Difference: ' + formatNumber(data.value, { type: 'currency', precision: 2 })}
					/>
					<TotalItem
						column="extendedPrice"
						summaryType="sum"
						customizeText={(data: any) => 'Total: ' + formatNumber(data.value, { type: 'currency', precision: 2 })}
					/>
					<TotalItem
						column="extendedPrice"
						name="priceDifference"
						summaryType="custom"
						customizeText={(data: any) => 'Difference: ' + formatNumber(data.value, { type: 'currency', precision: 2 })}
					/>
				</Summary>
			</DataGrid>

			<ToolbarItem
				options={{
					type: 'normal',
					stylingMode: 'contained',
					text: 'Print Change Request',
					elementAttr: { 'data-testid': 'btn-print-change-request' },
					onClick: () => { printReport(RetailOrderReportType.ChangeRequest); }
				}}
				toolbar={'bottom'}
				widget="dxButton"
				location="after"
			/>
			<ToolbarItem
				options={{
					type: 'normal',
					stylingMode: 'contained',
					text: 'Print Details',
					elementAttr: { 'data-testid': 'btn-print-details' },
					onClick: () => { printReport(RetailOrderReportType.ChangeRequestDetails); }
				}}
				toolbar={'bottom'}
				widget="dxButton"
				location="after"
			/>
			<ToolbarItem
				options={{
					type: 'normal',
					stylingMode: 'contained',
					text: 'Cancel',
					elementAttr: { 'data-testid': 'btn-cancel' },
					onClick: () => props.onHidePopup()
				}}
				toolbar={'bottom'}
				widget="dxButton"
				location="after"
			/>
			<LoadPopupPanel visible={loading} />
		</Popup>
	)
}
